import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Grid,
  InputNumber,
  Modal,
  Radio,
  Skeleton,
  Space,
  Steps,
  Typography,
} from 'antd';

import { AddressForm } from '@eshop/components/location/AddressForm';
import type { Address } from '@eshop/types/location.types.ts';
import type { ShippingMethod } from '@eshop/types/shipping.types.ts';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import type { PosShippingAddressEntry } from '../ShippingMethodCard';

export interface PosShippingSectionProps {
  visible: boolean;
  open: boolean;
  enabled: boolean;
  shippingDisabledHint: string | null;
  addresses: PosShippingAddressEntry[];
  selectedAddressId: string | null;
  methods: ShippingMethod[];
  methodsLoading: boolean;
  methodsError?: string | null;
  selectedMethodId: number | null;
  manualAmount: number | null;
  currency: string;
  customerName: string | null;
  onClose: () => void;
  onToggle: (enabled: boolean) => void;
  onSelectAddress: (addressId: string | null) => void;
  onCreateAddress: (address: Address) => Promise<PosShippingAddressEntry> | PosShippingAddressEntry;
  onRetryMethods: () => void;
  onSelectMethod: (methodId: number | null) => void;
  onManualAmountChange: (amount: number | null) => void;
}

const addressLabel = (address: Address): string =>
  [address.street, address.addr_detail, address.verbose?.city]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .join(' · ') || 'Dirección sin nombre';

const initialAddress = (customerName: string | null): Address => {
  const [firstName = '', ...rest] = (customerName ?? '').split(' ').filter(Boolean);
  return {
    first_name: firstName,
    last_name: rest.join(' '),
    street: '',
    addr_detail: '',
    country: 'AR',
    verbose: { city: '', state: '', postcode: '', country: 'Argentina' },
  };
};

const methodAmount = (method: ShippingMethod | null): number => {
  const raw = method?.calculated_cost?.incl_tax ?? method?.calculated_cost?.excl_tax;
  return Number.isFinite(Number(raw)) ? Number(raw) : 0;
};

export const PosShippingSection: React.FC<PosShippingSectionProps> = ({
  visible,
  open,
  enabled,
  shippingDisabledHint,
  addresses,
  selectedAddressId,
  methods,
  methodsLoading,
  methodsError,
  selectedMethodId,
  manualAmount,
  currency,
  customerName,
  onClose,
  onToggle,
  onSelectAddress,
  onCreateAddress,
  onRetryMethods,
  onSelectMethod,
  onManualAmountChange,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [step, setStep] = useState(0);
  const [draftEnabled, setDraftEnabled] = useState(enabled);
  const [draftAddressId, setDraftAddressId] = useState(selectedAddressId);
  const [draftMethodId, setDraftMethodId] = useState(selectedMethodId);
  const [draftManualAmount, setDraftManualAmount] = useState<number | null>(manualAmount);
  const [addressDrawerOpen, setAddressDrawerOpen] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const snapshotRef = useRef({ enabled, addressId: selectedAddressId, methodId: selectedMethodId, manualAmount });
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      snapshotRef.current = { enabled, addressId: selectedAddressId, methodId: selectedMethodId, manualAmount };
      setDraftEnabled(enabled);
      setDraftAddressId(selectedAddressId);
      setDraftMethodId(selectedMethodId);
      setDraftManualAmount(manualAmount);
      setStep(0);
    }
    wasOpenRef.current = open;
  }, [enabled, manualAmount, open, selectedAddressId, selectedMethodId]);

  const selectedAddress = addresses.find((entry) => entry.id === draftAddressId) ?? null;
  const selectedMethod = methods.find((method) => method.id === draftMethodId) ?? null;
  const isManualMethod = selectedMethod?.strategy === 'manual' || selectedMethod?.code === 'to_agree';
  const resolvedAmount = isManualMethod ? Number(draftManualAmount ?? 0) : methodAmount(selectedMethod);

  useEffect(() => {
    if (open && draftMethodId != null && !methods.some((method) => method.id === draftMethodId)) {
      setDraftMethodId(null);
    }
  }, [draftMethodId, methods, open]);

  const canContinue = useMemo(() => {
    if (step === 0) return !draftEnabled || !shippingDisabledHint;
    if (step === 1) return !draftEnabled || Boolean(draftAddressId);
    if (step === 2) return !draftEnabled || Boolean(draftMethodId);
    return true;
  }, [draftAddressId, draftEnabled, draftMethodId, shippingDisabledHint, step]);

  const cancel = () => {
    const previous = snapshotRef.current;
    onClose();
    onToggle(previous.enabled);
    onSelectAddress(previous.addressId);
    onSelectMethod(previous.methodId);
    onManualAmountChange(previous.manualAmount);
  };

  const confirm = () => {
    onToggle(draftEnabled);
    onSelectAddress(draftEnabled ? draftAddressId : null);
    onSelectMethod(draftEnabled ? draftMethodId : null);
    onManualAmountChange(draftEnabled && isManualMethod ? draftManualAmount : null);
    onClose();
  };

  const selectAddress = (id: string) => {
    setDraftAddressId(id);
    setDraftMethodId(null);
    onSelectAddress(id);
  };

  if (!visible) return null;

  const body = step === 0 ? (
    <Radio.Group value={draftEnabled ? 'shipping' : 'none'} onChange={(event) => setDraftEnabled(event.target.value === 'shipping')}>
      <Space orientation="vertical" size={12}>
        <Radio value="none">Sin envío</Radio>
        <Radio value="shipping" disabled={Boolean(shippingDisabledHint)}>Envío a domicilio</Radio>
        {shippingDisabledHint ? <Typography.Text type="secondary">{shippingDisabledHint}</Typography.Text> : null}
      </Space>
    </Radio.Group>
  ) : step === 1 ? (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      <Radio.Group value={draftAddressId ?? undefined} onChange={(event) => selectAddress(String(event.target.value))}>
        <Space orientation="vertical" size={10}>
          {addresses.map((entry) => <Radio key={entry.id} value={entry.id}>{addressLabel(entry.address)}</Radio>)}
        </Space>
      </Radio.Group>
      <Button onClick={() => setAddressDrawerOpen(true)}>Agregar dirección</Button>
      {addresses.length === 0 ? <Typography.Text type="secondary">No hay direcciones cargadas.</Typography.Text> : null}
    </Space>
  ) : step === 2 ? (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      {methodsError ? <Alert type="error" showIcon title={methodsError} action={<Button size="small" onClick={onRetryMethods}>Reintentar</Button>} /> : null}
      {methodsLoading ? <Skeleton active paragraph={{ rows: 2 }} /> : (
        <Radio.Group value={draftMethodId ?? undefined} onChange={(event) => setDraftMethodId(Number(event.target.value))}>
          <Space orientation="vertical" size={10}>
            {methods.map((method) => (
              <Radio key={method.id} value={method.id}>
                {method.name} · {(method.strategy === 'manual' || method.code === 'to_agree')
                  ? 'A convenir'
                  : getFormattedPrice(methodAmount(method), method.calculated_cost?.currency ?? currency)}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      )}
      {isManualMethod ? (
        <label className="pos-shipping-wizard__amount">
          <span>Importe acordado (opcional)</span>
          <InputNumber<number>
            min={0}
            precision={2}
            value={draftManualAmount}
            onChange={(value) => setDraftManualAmount(value == null ? null : Number(value))}
            addonBefore="$"
            placeholder="A convenir"
          />
        </label>
      ) : null}
    </Space>
  ) : (
    <dl className="pos-shipping-wizard__review">
      <div><dt>Entrega</dt><dd>{draftEnabled ? 'Envío a domicilio' : 'Sin envío'}</dd></div>
      {draftEnabled ? <><div><dt>Dirección</dt><dd>{selectedAddress ? addressLabel(selectedAddress.address) : 'Sin dirección'}</dd></div><div><dt>Método</dt><dd>{selectedMethod?.name ?? 'Sin método'}</dd></div><div><dt>Importe</dt><dd>{isManualMethod && resolvedAmount === 0 ? 'A convenir' : getFormattedPrice(resolvedAmount, currency)}</dd></div></> : null}
    </dl>
  );

  return (
    <>
      <Modal
        open={open}
        title="Configurar envío"
        width={isMobile ? '100%' : 560}
        onCancel={cancel}
        destroyOnHidden
        footer={(
          <div className="pos-shipping-wizard__footer">
            <Button onClick={step === 0 ? cancel : () => setStep((current) => current - 1)}>{step === 0 ? 'Cancelar' : 'Atrás'}</Button>
            <Button type="primary" disabled={!canContinue} onClick={step === 3 || !draftEnabled ? confirm : () => setStep((current) => current + 1)}>
              {step === 3 || !draftEnabled ? 'Confirmar' : 'Continuar'}
            </Button>
          </div>
        )}
      >
        <Steps size="small" current={step} items={[{ title: 'Entrega' }, { title: 'Dirección' }, { title: 'Método' }, { title: 'Revisar' }]} responsive />
        <div className="pos-shipping-wizard__body">{body}</div>
      </Modal>
      <Drawer
        placement="bottom"
        open={addressDrawerOpen}
        onClose={() => !savingAddress && setAddressDrawerOpen(false)}
        height="100%"
        title="Nueva dirección"
        destroyOnHidden
      >
        <AddressForm
          prefixKey="pos-shipping-wizard-address"
          initAddress={initialAddress(customerName)}
          onSubmit={async (address) => {
            setSavingAddress(true);
            try {
              const entry = await Promise.resolve(onCreateAddress(address));
              selectAddress(entry.id);
              setAddressDrawerOpen(false);
            } finally {
              setSavingAddress(false);
            }
          }}
          onCancel={() => setAddressDrawerOpen(false)}
          showActions
          submitLabel={savingAddress ? 'Guardando…' : 'Guardar dirección'}
          cancelLabel="Cancelar"
        />
      </Drawer>
    </>
  );
};

export default PosShippingSection;
